import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal
} from "@angular/core";
import {rxResource, takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {FormGroupDirective} from "@angular/forms";
import {sendCommandAndWait} from "../../../../../common/utils";
import {
  HarbourDuesDeclaration,
  HarbourDuesDeclarationStatus,
  HarbourDuesRotterdamProformaResponse
} from "@portbase/bezoekschip-service-typescriptmodels";
import {HarbourDuesUtilsService} from "../../../services/harbour-dues-utils.service";

@Component({
  selector: "app-harbour-dues-rotterdam-pro-forma",
  templateUrl: "./harbour-dues-rotterdam-pro-forma.component.html",
  styleUrl: "./harbour-dues-rotterdam-pro-forma.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HarbourDuesRotterdamProFormaComponent {
  private readonly form = inject(FormGroupDirective);
  private readonly destroyRef = inject(DestroyRef);
  private readonly harbourDuesUtilService = inject(HarbourDuesUtilsService);

  readonly selectedDeclaration = input.required<HarbourDuesDeclaration>();

  readonly proFormaRequested = output<void>();
  readonly validProFormaDisplayed = output<boolean>();

  protected readonly shouldReload = signal(false);
  protected readonly inputDeclaration = signal<HarbourDuesDeclaration & {cacheBuster: Date} | null>(null);
  // BSHD-342 Phase 7: proforma is a COMMAND now (it does a BTP call and records the returned status),
  // dispatched via sendCommandAndWait so it is never query-cached — every proforma request must hit BTP
  // afresh. The response payload still drives the resource (and validProFormaDisplayed) exactly as before.
  protected readonly proFormaResource = rxResource<HarbourDuesRotterdamProformaResponse, HarbourDuesDeclaration | null>({
    params: () => this.inputDeclaration(),
    stream: ({ params }) => sendCommandAndWait(
      "com.portbase.bezoekschip.common.api.harbourdues.commands.RequestRotterdamHarbourDuesProforma",
      params
    )
  });

  protected readonly isDeclared = computed(
    () => this.selectedDeclaration()?.status === HarbourDuesDeclarationStatus.DECLARED
  );

  constructor() {
    effect(() => {
      this.validProFormaDisplayed.emit(
        this.proFormaResource.hasValue() && !this.shouldReload()
      );
    });
  }

  ngOnInit(): void {
    const formGroup = this.form.control;
    formGroup.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      () => this.shouldReload.set(true)
    );
  }

  requestProForma(): void {
    this.proFormaRequested.emit();
    if (this.form.valid) {
      const mergedDeclaration = {
        ...this.harbourDuesUtilService.mergeDeclarationAndFormData(
          this.selectedDeclaration(),
          this.form.control.value
        ),
        cacheBuster: new Date()
      };
      this.inputDeclaration.set(mergedDeclaration);
      this.shouldReload.set(false);
    }
  }
}
